import { computed, type WritableComputedRef } from 'vue'

import { toIsoDate } from './dates'

const asIsoDate = (value: unknown) =>
  typeof value === 'string' && value
    ? value
    : value instanceof Date
      ? toIsoDate(value)
      : null

/**
 * `v-model` for a `DatePicker` of type `daterange` over two keys of a filter draft, which
 * the picker cannot bind to on its own: it reads and writes one `[from, to]` pair.
 *
 * The keys always hold `YYYY-MM-DD` — a picker without `value-format` hands over `Date`s,
 * and they are converted. Clearing the picker nulls both keys, and a draft holding only one
 * of them reads as no range: the picker cannot show half of one.
 *
 * **Date only.** Do not bind a `datetimerange` picker to it: the time is dropped on the way
 * in, so the picker and the draft would disagree the moment either side is given a
 * `value-format` carrying one. Such a filter writes its own model until this grows
 * an option.
 */
export function dateRangeModel<TDraft extends object>(
  draft: TDraft,
  fromKey: keyof TDraft,
  toKey: keyof TDraft
): WritableComputedRef<[string, string] | null> {
  return computed({
    get() {
      const from = asIsoDate(draft[fromKey])
      const to = asIsoDate(draft[toKey])

      return from && to ? [from, to] : null
    },
    set(value: unknown) {
      const range = Array.isArray(value) ? value : []

      draft[fromKey] = asIsoDate(range[0]) as TDraft[keyof TDraft]
      draft[toKey] = asIsoDate(range[1]) as TDraft[keyof TDraft]
    }
  })
}
